/*
 * AFX Mix & Coverage Calculator — core logic
 * ──────────────────────────────────────────────────────────────────────────
 * Pure, dependency-free computation for paint/coating mix ratios and
 * coverage. Shipped as a theme asset and consumed by the
 * afx-mix-calculator.liquid section. Also runnable under Node for tests
 * (CommonJS export at the bottom).
 *
 * Refinish semantics (the math has to match a real mixing stick):
 *   A ratio "4:1:1" means 4 parts base/color : 1 part hardener/activator :
 *   1 part reducer. Component 0 is always the base. Painters most often
 *   measure by the base they intend to spray ("1 quart of color"), so the
 *   primary mode is "from base".
 *
 * Volume units are kept in fluid ounces internally; helpers convert to/from
 * common shop units (ml, pint, quart, gallon, liter).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;            // Node / tests
  } else {
    root.AFXMix = api;               // browser global
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ── Unit conversion (everything pivots through fluid ounces) ────────────
  var OZ = {
    oz: 1,
    floz: 1,
    ml: 1 / 29.5735295625,
    l: 1000 / 29.5735295625,
    liter: 1000 / 29.5735295625,
    pt: 16,
    pint: 16,
    qt: 32,
    quart: 32,
    gal: 128,
    gallon: 128
  };

  function toOz(value, unit) {
    var f = OZ[String(unit || 'oz').toLowerCase()];
    if (!f || !isFinite(value)) return NaN;
    return value * f;
  }

  function fromOz(oz, unit) {
    var f = OZ[String(unit || 'oz').toLowerCase()];
    if (!f || !isFinite(oz)) return NaN;
    return oz / f;
  }

  // ── Ratio parsing ───────────────────────────────────────────────────────
  // Accepts "4:1:1", "4 : 1 : 1", "2:1", "4-1-1", "100:25:10", "3:1:1.5".
  // Returns an array of positive numbers (>= 2 parts) or null if unusable.
  function parseRatio(str) {
    if (str == null) return null;
    var raw = String(str).trim();
    if (!raw) return null;
    // Normalise separators: treat ':', '-', '/', or whitespace as delimiters,
    // but only when the whole string looks like a numeric ratio.
    var cleaned = raw.replace(/\s*to\s*/gi, ':');
    var parts = cleaned.split(/[:/\-]| +/).filter(function (p) { return p !== ''; });
    if (parts.length < 2) return null;
    var nums = [];
    for (var i = 0; i < parts.length; i++) {
      var n = parseFloat(parts[i]);
      if (!isFinite(n) || n <= 0) return null;
      nums.push(n);
    }
    return nums;
  }

  function ratioSum(parts) {
    var s = 0;
    for (var i = 0; i < parts.length; i++) s += parts[i];
    return s;
  }

  // ── Core computations (unit-agnostic; caller decides the unit) ──────────

  // Given the amount of the BASE (component 0), return every component's
  // amount in the same unit. e.g. ratioFromBase([4,1,1], 32) -> [32, 8, 8].
  function ratioFromBase(parts, baseAmount) {
    if (!parts || parts.length < 2 || !isFinite(baseAmount) || baseAmount < 0) return null;
    var base0 = parts[0];
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      out.push(baseAmount * (parts[i] / base0));
    }
    return out;
  }

  // Given the desired TOTAL mixed amount, split across components by ratio.
  // ratioFromTotal([4,1,1], 48) -> [32, 8, 8].
  function ratioFromTotal(parts, total) {
    if (!parts || parts.length < 2 || !isFinite(total) || total < 0) return null;
    var sum = ratioSum(parts);
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      out.push(total * (parts[i] / sum));
    }
    return out;
  }

  // Coverage-driven job sizing. coveragePerGal = ready-to-spray sq ft per
  // gallon (typical refinish numbers ~150–300). Returns ready-to-spray
  // gallons needed for the job (area * coats / coverage), unrounded.
  function gallonsForJob(coveragePerGal, areaSqFt, coats) {
    coats = coats || 1;
    if (!isFinite(coveragePerGal) || coveragePerGal <= 0) return null;
    if (!isFinite(areaSqFt) || areaSqFt < 0) return null;
    return (areaSqFt * coats) / coveragePerGal;
  }

  // Full job plan from coverage: how much of each component to mix.
  // Returns { gallons, totalOz, amountsOz: [...] }.
  function planFromCoverage(parts, coveragePerGal, areaSqFt, coats) {
    var gal = gallonsForJob(coveragePerGal, areaSqFt, coats);
    if (gal == null) return null;
    var totalOz = gal * OZ.gal;
    return {
      gallons: gal,
      totalOz: totalOz,
      amountsOz: ratioFromTotal(parts, totalOz)
    };
  }

  // ── Component labelling ─────────────────────────────────────────────────
  // Sensible defaults when the product doesn't name its components.
  var DEFAULT_LABELS = {
    2: ['Base / Color', 'Hardener'],
    3: ['Base / Color', 'Hardener', 'Reducer'],
    4: ['Base / Color', 'Hardener', 'Reducer', 'Additive']
  };

  function labelsFor(parts, provided) {
    if (provided && provided.length === parts.length) return provided.slice();
    var def = DEFAULT_LABELS[parts.length];
    if (def) return def.slice();
    // Fallback: Part A, Part B, ...
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      out.push('Part ' + String.fromCharCode(65 + i));
    }
    return out;
  }

  // ── Display formatting ──────────────────────────────────────────────────
  function round(n, dp) {
    if (!isFinite(n)) return 0;
    var f = Math.pow(10, dp == null ? 1 : dp);
    return Math.round(n * f) / f;
  }

  // Format an ounce amount into a friendly string in the requested unit.
  function fmt(oz, unit, dp) {
    var v = fromOz(oz, unit);
    return round(v, dp) + ' ' + unitLabel(unit);
  }

  function unitLabel(unit) {
    var u = String(unit || 'oz').toLowerCase();
    var map = { oz: 'oz', floz: 'oz', ml: 'ml', l: 'L', liter: 'L', pt: 'pt', pint: 'pt', qt: 'qt', quart: 'qt', gal: 'gal', gallon: 'gal' };
    return map[u] || u;
  }

  // ── Shareable recipe state ⇄ URL query string ───────────────────────────
  // Lets the standalone calculator emit a deep-link (for "copy link", print
  // card and QR) and restore itself when that link is opened. Only the fields
  // relevant to the active mode are serialised. ':' is kept literal so the URL
  // stays human-readable (it is a valid query character).
  var MODES = { amount: 1, job: 1, have: 1 };
  function enc(v) { return encodeURIComponent(v).replace(/%3A/g, ':'); }

  function encodeRecipe(s) {
    if (!s) return '';
    var mode = MODES[s.mode] ? s.mode : 'amount';
    var p = ['m=' + mode];
    if (s.ratio && parseRatio(s.ratio)) p.push('r=' + enc(s.ratio));
    function add(k, v) { if (v != null && v !== '' && isFinite(v)) p.push(k + '=' + enc(v)); }
    if (mode === 'amount') { add('b', s.base); if (s.unit) p.push('u=' + enc(s.unit)); }
    else if (mode === 'job') {
      add('a', s.area); add('c', s.coats); add('cov', s.cov);
      if (s.over) p.push('o=1');
      if (s.covUnit && s.covUnit !== 'oz') p.push('ru=' + enc(s.covUnit));
    } else if (mode === 'have') {
      add('h', s.have); if (s.haveUnit) p.push('hu=' + enc(s.haveUnit));
      add('hc', s.haveComp);
    }
    return p.join('&');
  }

  function decodeRecipe(query) {
    if (query == null) return null;
    var q = String(query).replace(/^[?#]/, '');
    if (!q) return null;
    var out = {}, found = false;
    q.split('&').forEach(function (pair) {
      if (!pair) return;
      var i = pair.indexOf('='), k = i < 0 ? pair : pair.slice(0, i);
      var v = i < 0 ? '' : decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' '));
      var n = parseFloat(v);
      switch (k) {
        case 'm': if (MODES[v]) { out.mode = v; found = true; } break;
        case 'r': var pr = parseRatio(v); if (pr) { out.ratio = pr.join(':'); found = true; } break;
        case 'b': if (isFinite(n) && n >= 0) { out.base = n; found = true; } break;
        case 'u': if (v) { out.unit = v; found = true; } break;
        case 'a': if (isFinite(n) && n >= 0) { out.area = n; found = true; } break;
        case 'c': if (isFinite(n) && n >= 1) { out.coats = n; found = true; } break;
        case 'cov': if (isFinite(n) && n > 0) { out.cov = n; found = true; } break;
        case 'ru': if (['gal', 'qt', 'oz', 'ml', 'l'].indexOf(v) >= 0) { out.covUnit = v; found = true; } break;
        case 'o': if (v === '1' || v === 'true' || v === '0' || v === 'false') { out.over = (v === '1' || v === 'true'); found = true; } break;
        case 'h': if (isFinite(n) && n >= 0) { out.have = n; found = true; } break;
        case 'hu': if (v) { out.haveUnit = v; found = true; } break;
        case 'hc': if (isFinite(n) && n >= 0) { out.haveComp = Math.floor(n); found = true; } break;
      }
    });
    return found ? out : null;
  }

  return {
    OZ: OZ,
    toOz: toOz,
    fromOz: fromOz,
    parseRatio: parseRatio,
    ratioSum: ratioSum,
    ratioFromBase: ratioFromBase,
    ratioFromTotal: ratioFromTotal,
    gallonsForJob: gallonsForJob,
    planFromCoverage: planFromCoverage,
    labelsFor: labelsFor,
    round: round,
    fmt: fmt,
    unitLabel: unitLabel,
    encodeRecipe: encodeRecipe,
    decodeRecipe: decodeRecipe
  };
});
